/* eslint-disable react-hooks/exhaustive-deps */
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { opacity2hex, sortArray } from "utils/miscUtils";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getBannerTableColums } from "utils/tableHelper/bannerTableHelper";
import { formatUtcDateTime } from "utils/contentSchedule";
import sessionHelper from "services/sessionHelper";
import {
  archiveBanners,
  bulkDeleteBanners,
  bulkUpdateBannerStatus,
  copyHomeBanner,
  deleteBanner,
  getBanners,
  unarchiveBanners,
  updateBannerStatus,
} from "services/homeBannerService";
import ArchiveActionBar from "components/archive/ArchiveActionBar";
import ArchiveViewButton from "components/archive/ArchiveViewButton";
import useContentArchive from "components/archive/useContentArchive";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import ViewImageModal from "screens/modals/ViewImageModal";
import useOpenClose from "hooks/useOpenClose";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import useConsent from "hooks/useConsent";

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: {
    handleDisble?: (id: any) => void;
    handleDelete?: (data: any) => void;
    handleCopyHomeBanner?: (id: any) => void;
    handleArchive?: (ids: string[]) => void;
    archived?: boolean;
  };
}

const useStyles = makeStyles((theme) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
}));

const RowActions = ({
  rowId,
  data,
  actionHandlers: {
    handleDisble = () => {},
    handleDelete = () => {},
    handleCopyHomeBanner = () => {},
    handleArchive = () => {},
    archived = false,
  },
}: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEditClick = (event: any, data: any) => {
    handleClose(event);
    navigate(configData.routes.homeBanners.edit?.replace(":id", data?._id));
  };

  const handleDisbleClick = (event: any, id: any) => {
    handleClose(event);
    handleDisble(id);
  };

  const handleCopyHomeBannerClick = (event: any, id: any) => {
    handleClose(event);
    handleCopyHomeBanner(id);
  };

  const handleDeleteClick = (event: any, rowData: any) => {
    handleClose(event);
    handleDelete(rowData);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.BANNERS.DELETE_ROLES,
  );

  const handleArchiveClick = (event: any, id: any) => {
    handleClose(event);
    handleArchive([id]);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {archived ? (
          <MenuItem onClick={(e) => handleArchiveClick(e, data?._id)}>
            Restore
          </MenuItem>
        ) : (
          [
            <MenuItem key="edit" onClick={(e) => handleEditClick(e, data)}>
              Edit
            </MenuItem>,
            <MenuItem
              key="status"
              onClick={(e) => handleDisbleClick(e, data?._id)}
            >
              {data?.isActive ? "Disable" : "Enable"}
            </MenuItem>,
            <MenuItem
              key="copy"
              onClick={(e) => handleCopyHomeBannerClick(e, data?._id)}
            >
              Copy
            </MenuItem>,
            <MenuItem
              key="archive"
              disabled={data?.isActive}
              onClick={(e) => handleArchiveClick(e, data?._id)}
            >
              Archive
            </MenuItem>,
            hasDeletePermission ? (
              <MenuItem
                key="delete"
                disabled={data?.isActive}
                onClick={(e) => handleDeleteClick(e, data)}
              >
                Delete
              </MenuItem>
            ) : null,
          ]
        )}
      </Menu>
    </>
  );
};

const HomeBanners = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [listData, setListData] = useState([]);
  const { isOpen, close, open, extraData, updateExtra } = useOpenClose<{
    imageUrl?: string;
  }>();
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const [orderDirection, setOrderDirection] = useState("asc");

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const onSearch = (value: any) => {
    // reset filters if any
    onChangePage(defaultPageNo);
    _onSearch(value);
  };

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.BANNERS.UPDATE_ROLES,
  );
  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.BANNERS.DELETE_ROLES,
  );

  const createBanner = () => navigate(configData.routes.homeBanners.create);
  const navigateToBulkUpload = () =>
    navigate(configData.routes.homeBanners.bulkUpload);

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: listData?.length });

  const archive = useContentArchive({
    label: { singular: "banner", plural: "banners" },
    isLive: (row: any) => !!row?.isActive,
    archive: archiveBanners,
    unarchive: unarchiveBanners,
    bulkStatus: bulkUpdateBannerStatus,
    bulkDelete: hasDeletePermission ? bulkDeleteBanners : undefined,
    rows: listData,
    onCompleted: () => fetchData(searchValue),
    onViewChange: () => onChangePage(defaultPageNo),
  });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const onDelete = async () => {
    try {
      const resp = await deleteBanner(constentDetails?.id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const onCopy = async (id: any) => {
    try {
      setLoading(true);
      const resp = await copyHomeBanner(id);
      await fetchData();
      showSnackbar(resp?.message);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?.title ?? data?._id, id: data?._id });
  };

  const onDisable = async (bannerId) => {
    try {
      const resp = await updateBannerStatus(bannerId);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const fetchData = async (_search?: any) => {
    setLoading(true);
    try {
      let payload: any = { pageNo, pageSize, archived: archive.archived };
      if (searchValue) payload["search"] = searchValue;
      const resp = await getBanners(payload);

      setListData(
        resp?.data?.items?.map((item) => ({
          ...item,
          isActiveText: item.isActive ? "YES" : "NO",
          rowDisabled: !item?.isActive,
          copiedFrom: item?.copiedFrom || "-",
          startDateText: formatUtcDateTime(item?.startDate),
          endDateText: formatUtcDateTime(item?.endDate),
        })) || [],
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const openModel = (data: any) => {
    updateExtra({
      imageUrl: data.imageUrl,
    });
    open();
  };

  // Archived records are a read-only view, so their rows do not link into the editor.
  const rowHref = (data: any) =>
    hasEditPermission && !archive.archived
      ? configData.routes.homeBanners.edit?.replace(":id", data?._id)
      : null;

  useEffect(() => {
    const cols = getBannerTableColums().filter(
      (col) => col.id !== "actions" || hasEditPermission,
    );
    setColumns(cols);
    fetchData();
  }, [pageNo, pageSize, searchValue, archive.archived]);

  return (
    <ContentShell
      title={archive.archived ? "Archived Home Banners" : "Home Banners"}
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a banner"
            onSearch={onSearch}
          />
          <ArchiveViewButton archive={archive} />
          {!archive.archived &&
            sessionHelper.hasRole(PermittedRoles.BANNERS.CREATE_ROLES) && (
              <CustomButton onClick={navigateToBulkUpload}>
                Bulk Upload
              </CustomButton>
            )}
          {!archive.archived &&
            sessionHelper.hasRole(PermittedRoles.BANNERS.CREATE_ROLES) && (
              <CustomButton onClick={createBanner}>Create</CustomButton>
            )}
        </Box>
      }
    >
      <Box className={styles.container}>
        <ArchiveActionBar archive={archive} rows={listData} nameKey="title" />
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={listData.length}
          highlightAltRow={true}
          actions={RowActions}
          rowHref={rowHref}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          onColumnClick={onColumnClick}
          selectable={hasEditPermission}
          selectedIds={archive.selectedIds}
          onSelectionChange={archive.setSelectedIds}
          rowSelectDisabledReason={archive.rowSelectDisabledReason}
          selectAllOptions={archive.selectAllOptions}
          actionHandlers={{
            openModal: openModel,
            handleDisble: onDisable,
            handleDelete: _openConsentDialog,
            handleCopyHomeBanner: onCopy,
            handleArchive: archive.requestAction,
            archived: archive.archived,
          }}
        />
        <ViewImageModal
          label="Banner Image"
          imageSource={extraData.imageUrl}
          isOpen={isOpen}
          close={close}
          customStyle={{ width: "60%" }}
        />
        <DeleteConsentDialog
          itemName={constentDetails?.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
    </ContentShell>
  );
};

export default HomeBanners;
